/**
 * Product Description Accordion Transformer
 *
 * Automatically converts product descriptions with h3 headings into
 * collapsible accordion sections. Each h3 element becomes an accordion
 * header, and all following content until the next h3 becomes that
 * accordion's content.
 *
 * Usage: Add data-accordion-transform="true" to product__description div
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    firstAccordionOpen: true,  // Auto-open first accordion
    preserveAllElements: true,  // Keep all HTML elements (lists, images, etc.)
    fallbackBehavior: 'keep',   // Keep original if no h3 found
  };

  /**
   * Main transformation function
   */
  function transformProductDescription() {
    const descriptionDiv = document.querySelector('.product__description[data-accordion-transform="true"]');

    // Exit if no description to transform
    if (!descriptionDiv || !descriptionDiv.innerHTML.trim()) {
      return;
    }

    try {
      const sections = parseHTML(descriptionDiv);

      // No h3 sections found - keep original description
      if (sections.length === 0) {
        if (CONFIG.fallbackBehavior === 'keep') {
          return;
        }
      }

      const accordionHTML = generateAccordions(sections, descriptionDiv);
      descriptionDiv.innerHTML = accordionHTML;
      descriptionDiv.classList.add('product__description--accordion');

      initializeAccordions(descriptionDiv);
    } catch (error) {
      console.error('Failed to transform product description:', error);
      // Keep original content on error (graceful degradation)
    }
  }

  /**
   * Parse HTML content into sections based on h3 headings
   * @param {HTMLElement} container - The description container
   * @returns {Array} Array of section objects {heading, content}
   */
  function parseHTML(container) {
    const sections = [];
    let currentSection = null;

    // Get all direct children
    Array.from(container.children).forEach((element) => {
      if (element.tagName === 'H3') {
        // New section found - save previous section if exists
        if (currentSection) {
          sections.push(currentSection);
        }

        // Create new section
        currentSection = {
          heading: element.textContent.trim(),
          content: []
        };
      } else if (currentSection) {
        // Add content to current section
        if (CONFIG.preserveAllElements || element.tagName !== 'SCRIPT') {
          currentSection.content.push(element.outerHTML);
        }
      }
      // Note: Content before first h3 is ignored
      // To preserve it, you could add it to a default section
    });

    // Push final section
    if (currentSection && currentSection.content.length > 0) {
      sections.push(currentSection);
    }

    return sections;
  }

  /**
   * Generate accordion HTML from sections
   * @param {Array} sections - Array of section objects
   * @param {HTMLElement} container - Original container for getting section ID
   * @returns {String} Complete accordion HTML
   */
  function generateAccordions(sections, container) {
    // Generate unique section ID based on timestamp
    const sectionId = Date.now();

    // Try to get actual section ID from Shopify attributes if available
    const shopifySection = container.closest('[id*="shopify-section"]');
    const actualSectionId = shopifySection ? shopifySection.id.replace('shopify-section-', '') : sectionId;

    return sections.map((section, index) => {
      const detailsId = `Details-description-${index}-${actualSectionId}`;
      const contentId = `ProductAccordion-description-${index}-${actualSectionId}`;
      const openAttr = (CONFIG.firstAccordionOpen && index === 0) ? 'open' : '';

      return `
      <div class="product__accordion accordion quick-add-hidden">
        <details id="${detailsId}" ${openAttr}>
          <summary>
            <div class="summary__title">
              <h2 class="h4 accordion__title">
                ${escapeHtml(section.heading)}
              </h2>
            </div>
            ${getCaretIcon()}
          </summary>
          <div class="accordion__content rte" id="${contentId}">
            ${section.content.join('\n')}
          </div>
        </details>
      </div>
    `.trim();
    }).join('\n');
  }

  /**
   * Get caret icon SVG for accordion
   * @returns {String} SVG markup for caret icon
   */
  function getCaretIcon() {
    // Use theme's standard caret icon
    return `<svg class="icon icon-caret" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
    </svg>`;
  }

  /**
   * Escape HTML to prevent XSS in heading text
   * @param {String} text - Text to escape
   * @returns {String} Escaped text
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Initialize accordion functionality and accessibility
   * @param {HTMLElement} container - Container with accordions
   */
  function initializeAccordions(container) {
    // Setup aria attributes for accessibility
    container.querySelectorAll('summary').forEach((summary) => {
      // Add click handler for aria-expanded
      summary.addEventListener('click', (event) => {
        // Store reference before setTimeout (event.currentTarget becomes null after event completes)
        const details = event.currentTarget.closest('details');
        if (!details) return;
        // Use setTimeout to let the details element update first
        setTimeout(() => {
          const isOpen = details.hasAttribute('open');
          summary.setAttribute('aria-expanded', isOpen);
        }, 0);
      });

      // Set initial aria-expanded state
      const details = summary.closest('details');
      const isOpen = details.hasAttribute('open');
      summary.setAttribute('aria-expanded', isOpen);

      // Add role for better accessibility
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-controls', details.querySelector('.accordion__content').id);
    });
  }

  /**
   * Initialize when DOM is ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', transformProductDescription);
    } else {
      // DOM already loaded
      transformProductDescription();
    }
  }

  // Start initialization
  init();

})();
